// ─────────────────────────────────────────────────────────────────────────────
// the landing page's flagship feature. this took longer than i would like to admit.
// ─────────────────────────────────────────────────────────────────────────────

import {
  WebGLRenderer,
  Scene,
  PerspectiveCamera,
  Group,
  PlaneGeometry,
  ShaderMaterial,
  Mesh,
  Vector2,
  Vector3,
  Raycaster,
  TextureLoader,
  CanvasTexture,
  SRGBColorSpace,
  MathUtils,
  DoubleSide,
  Texture,
} from "three";

import {
  vertexShader,
  fragmentShader,
} from "./shader/shaders";

import type { SpiralItem, SpiralApi, SpiralTween } from "./types";
import { resolvePhoto } from "./content";

const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const TWO_PI = Math.PI * 2;
const PI = Math.PI;

// card dimensions (world units), preserve aspect ratio
const BASE_CARD_W = 1.45;
const BASE_CARD_H = 0.85;
const CARD_ASPECT = BASE_CARD_W / BASE_CARD_H;

// spacing constants for the helix.
const VERT_GAP = 0.34; // vertical gap between cards (world units at reference scale)
const ANG_GAP = 0.85; // angular step between cards (rad)

// auto-rotate speed
const AUTO_ROTATE = REDUCED ? 0 : 0.002;

// ── placeholder card generation ───────────────────────────────────────────────
function shadeHex(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `rgb(${clamp(r + amount)},${clamp(g + amount)},${clamp(b + amount)})`;
}

function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  return 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
}

function makePlaceholderTexture(item: SpiralItem): Texture {
  const w = 768;
  const h = Math.round(w / CARD_ASPECT);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  const col = item.color || "#ff6a2b";

  // background gradient
  const bg = ctx.createLinearGradient(0, 0, w, h);
  bg.addColorStop(0, shadeHex(col, 35));
  bg.addColorStop(1, shadeHex(col, -78));
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  // highlight glow
  const hl = ctx.createRadialGradient(w * 0.26, h * 0.2, 10, w * 0.26, h * 0.2, w * 0.7);
  hl.addColorStop(0, "rgba(255,255,255,0.22)");
  hl.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = hl;
  ctx.fillRect(0, 0, w, h);

  // vignette
  const vg = ctx.createRadialGradient(w * 0.5, h * 0.5, h * 0.3, w * 0.5, h * 0.5, w * 0.7);
  vg.addColorStop(0, "rgba(0,0,0,0)");
  vg.addColorStop(1, "rgba(0,0,0,0.32)");
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, w, h);

  // subtle grid overlay
  ctx.strokeStyle = "rgba(255,255,255,0.07)";
  ctx.lineWidth = 1;
  for (let x = 0; x <= w; x += 56) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
  for (let y = 0; y <= h; y += 56) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }

  // text — ink colour chosen for contrast against card colour
  const ink = luminance(col) > 165 ? "#15120a" : "#ffffff";
  ctx.shadowColor = "rgba(0,0,0,0.28)";
  ctx.shadowBlur = 12;
  ctx.shadowOffsetY = 2;
  ctx.fillStyle = ink;
  ctx.font = "600 132px 'Space Grotesk', Arial";
  ctx.textBaseline = "top";
  ctx.fillText(item.year ?? "", 44, 30);

  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.globalAlpha = 0.85;
  ctx.font = "500 26px 'IBM Plex Mono', monospace";
  ctx.fillText((item.kind || "event").toUpperCase(), 46, 182);
  ctx.globalAlpha = 1;

  // word-wrapped title at the bottom
  ctx.font = "500 48px 'Space Grotesk', Arial";
  const words = (item.title || "").split(" ");
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    if ((line + word).length > 20) { lines.push(line.trim()); line = word + " "; }
    else line += word + " ";
  }
  lines.push(line.trim());
  const lineHeight = 54;
  let ty = h - 40 - lines.length * lineHeight;
  ctx.shadowColor = "rgba(0,0,0,0.35)";
  ctx.shadowBlur = 14;
  for (const l of lines) { ctx.fillText(l, 46, ty); ty += lineHeight; }
  ctx.shadowBlur = 0;

  // play button badge for video cards
  if (item.kind === "video") {
    ctx.fillStyle = "rgba(255,255,255,0.92)";
    ctx.beginPath(); ctx.arc(w - 88, h - 88, 46, 0, TWO_PI); ctx.fill();
    ctx.fillStyle = shadeHex(col, -60);
    ctx.beginPath(); ctx.moveTo(w - 100, h - 114); ctx.lineTo(w - 100, h - 62); ctx.lineTo(w - 56, h - 88); ctx.closePath(); ctx.fill();
  }

  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  return tex;
}

// ── layout geometry ──────────────────────────────────
interface Layout {
  mobile: boolean;
  wide: boolean;
  short: boolean;
  cardW: number;
  cardH: number;
  radius: number;
  vertGap: number;
  angGap: number;
  camZ: number;
  fov: number;
  groupX: number;
  yOffset: number;
  fadeBot0: number;
  fadeBot1: number;
  bend: number;
  viewHalfH: number;
}

function layoutFor(viewW: number, viewH: number): Layout {
  const mobile = viewW < 760;
  const wide = viewW / viewH > 1.05;
  const short = viewH < 640;
  const vmin = Math.min(viewW, viewH);

  // t scales card/camera sizes across the viewport range [0.5, 1.18]
  const t = MathUtils.clamp(vmin / 900, 0.5, 1.18);

  let cardScale: number;
  let camZ: number;
  let fov: number;
  let groupX: number;
  let deskNarrow = 1; // 1 at less than 1400 px, 0.5 on smol laptops

  if (mobile) {
    // closer cam on mobile
    fov = 46;
    camZ = MathUtils.lerp(4.7, 4.1, t);
    cardScale = 1.0;
    groupX = 0;
  } else {
    deskNarrow = MathUtils.clamp((viewW - 640) / 760, 0.5, 1);
    fov = 35;
    camZ = MathUtils.lerp(10.2, 7.8, t) + (wide ? 0 : 0.4);
    cardScale = MathUtils.lerp(0.58, 1, deskNarrow);
    groupX = 0; // set from radius below
  }

  const cardW = BASE_CARD_W * t * cardScale;
  const cardH = BASE_CARD_H * t * cardScale;

  const radius = cardW * 1.12;
  const vertGap = cardH * (VERT_GAP / BASE_CARD_H) * (short ? 0.86 : 1);

  if (!mobile) groupX = -radius * (0.62 + (1 - deskNarrow) * 0.32);

  // world-space half-height visible at the cylinder axis
  const viewHalfH = Math.tan((fov * PI) / 360) * camZ;

  const facingOffset = (PI / 2) / ANG_GAP;
  const faceY = mobile ? viewHalfH * 0.12 : 0;
  const yOffset = -facingOffset * vertGap + faceY;

  // Bottom dissolve thresholds. Cards below fadeBot0 begin fading, and are
  // fully gone at fadeBot1. On phones this hides the lower coil before it
  // reaches the hero title (hacky workaround)
  const fadeBot0 = mobile ? faceY - viewHalfH * 0.02 : -viewHalfH * 0.6;
  const fadeBot1 = mobile ? faceY - viewHalfH * 0.24 : -viewHalfH * 0.98;

  return {
    mobile, wide, short,
    cardW, cardH, radius, vertGap,
    angGap: ANG_GAP,
    camZ, fov, groupX,
    yOffset, fadeBot0, fadeBot1,
    bend: 0.2 * t,
    viewHalfH,
  };
}

// ── card meshes ─────────────────────────────────
interface CardData {
  i: number;        // index into source items array
  slot: number;     // pos
  item: SpiralItem;
  hover: number;
  alpha: number;
  hidden: number;
}

function hexToRgbVec(hex: string): [number, number, number] {
  const n = parseInt((hex || "#ff6a2b").slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

// ── exposed spiral api ─────────────────────────────────────────────────────────────────
export function createSpiral(
  canvas: HTMLCanvasElement | null,
  {
    items = [] as SpiralItem[],
    onHover,
    onSelect,
  }: {
    items?: SpiralItem[];
    onHover?: (item: SpiralItem | null, mesh: Mesh | null) => void;
    onSelect?: (item: SpiralItem, index: number) => void;
  } = {}
): SpiralApi | null {
  if (!canvas || !items.length) return null;

  const sourceN = items.length;

  // how many copies of source items exist
  const totalCount = sourceN * 3;
  const centerIndex = Math.floor(totalCount / 2);

  // ── render the dang thing ────────────────────────────────────────────────────────────────
  const renderer = new WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
    powerPreference: "high-performance",
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setClearColor(0x000000, 0);
  renderer.outputColorSpace = SRGBColorSpace;

  const scene = new Scene();
  const camera = new PerspectiveCamera(35, 1, 0.1, 100);
  camera.position.set(0, 0, 8);

  const group = new Group();
  scene.add(group);

  const cardGeo = new PlaneGeometry(1, 1, 32, 8);
  const texLoader = new TextureLoader();
  const cards: Mesh[] = [];
  let layout = layoutFor(window.innerWidth, window.innerHeight);

  // ── build card meshes ────────────────────────────────────────────────────────
  for (let i = 0; i < totalCount; i++) {
    const item = items[i % sourceN];

    const mat = new ShaderMaterial({
      uniforms: {
        uMap:        { value: null as Texture | null },
        uHover:      { value: 0 },
        uShow:       { value: 0 },
        uReveal:     { value: REDUCED ? 1 : 0 },
        uScrollSpeed:{ value: 0 },
        uBend:       { value: layout.bend },
        uDepth:      { value: 0.5 },
        uHasImage:   { value: 0 },
        uCardAspect: { value: CARD_ASPECT },
        uImgAspect:  { value: CARD_ASPECT },
        uAccent:     { value: new Vector3(1, 0.42, 0.17) },
        uColor:      { value: new Vector3(...hexToRgbVec(item.color)) },
      },
      vertexShader,
      fragmentShader,
      transparent: true,
      side: DoubleSide,
      depthWrite: false,
      depthTest: false,
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (mat as any).extensions = { derivatives: true };

    const mesh = new Mesh(cardGeo, mat);
    mesh.scale.set(layout.cardW, layout.cardH, 1);
    mesh.userData = {
      i: i % sourceN,
      slot: i,
      item,
      hover: 0,
      alpha: 0,
      hidden: 0,
    } satisfies CardData;

    group.add(mesh);
    cards.push(mesh);

    // Load photo texture; fall back to a generated placeholder on err
    if (item.image) {
      texLoader.setCrossOrigin("anonymous");
      texLoader.load(
        resolvePhoto(item.image),
        (tx) => {
          tx.colorSpace = SRGBColorSpace;
          mat.uniforms["uMap"].value = tx;
          mat.uniforms["uImgAspect"].value = (tx.image as HTMLImageElement).width / (tx.image as HTMLImageElement).height;
          mat.uniforms["uHasImage"].value = 1;
        },
        undefined,
        () => { mat.uniforms["uMap"].value = makePlaceholderTexture(item); }
      );
    } else {
      mat.uniforms["uMap"].value = makePlaceholderTexture(item);
    }
  }

  // ── input state ──────────────────────────────────────────────────────────────
  const raycaster = new Raycaster();
  const ndcPointer = new Vector2(2, 2); // start off-screen

  let scrollOffset = 0;
  let wheelDelta = 0;
  let wheelDeltaTarget = 0;
  let wheelDirection = 1;

  let isPointerDown = false;
  let pointerDownX = 0;
  let pointerDownY = 0;
  let lastPointerX = 0;
  let lastInteractTime = performance.now();

  let hoveredIndex = -1;

  const tween: SpiralTween = {
    reveal: REDUCED ? 1 : 0,
    dim: 0,
    dimTarget: 0,
  };

  // ── raycast from pointer ───────────────────────────────────────────────────────────────
  function updateNdc(e: PointerEvent): void {
    const rect = canvas!.getBoundingClientRect();
    ndcPointer.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -(((e.clientY - rect.top) / rect.height) * 2 - 1)
    );
  }

  function pickCard(): number {
    group.updateMatrixWorld(true);
    raycaster.setFromCamera(ndcPointer, camera);
    const hits = raycaster
      .intersectObjects(cards, false)
      .filter((h) => (h.object.userData as CardData).alpha > 0.25);
    return hits.length ? (hits[0].object.userData as CardData).i : -1;
  }

  // ── event listener ──────────────────────────────────────────────────
  canvas.addEventListener("pointerdown", (e) => {
    if (tween.dimTarget > 0.5) return;
    isPointerDown = true;
    pointerDownX = lastPointerX = e.clientX;
    pointerDownY = e.clientY;
    wheelDeltaTarget = 0;
    canvas!.setPointerCapture?.(e.pointerId);
    lastInteractTime = performance.now();
  });

  window.addEventListener("pointermove", (e) => {
    updateNdc(e);
    if (!isPointerDown) return;
    const isTouch = e.pointerType === "touch";
    const dx = (e.clientX - lastPointerX) * (isTouch ? -1 : 1);
    lastPointerX = e.clientX;
    scrollOffset -= dx * (isTouch ? 0.0075 : 0.006);
    wheelDeltaTarget = MathUtils.clamp(-dx * 0.00035, -2, 2);
    wheelDirection = dx > 0 ? -1 : 1;
    lastInteractTime = performance.now();
  }, { passive: true });

  window.addEventListener("pointerup", (e) => {
    if (!isPointerDown) return;
    isPointerDown = false;
    const dist = Math.hypot(e.clientX - pointerDownX, e.clientY - pointerDownY);
    // short tap = select card
    if (dist < 6 && tween.dimTarget < 0.5) {
      updateNdc(e);
      const i = pickCard();
      if (i >= 0) onSelect?.(items[i], i);
    }
  });

  canvas.addEventListener("wheel", (e) => {
    if (tween.dimTarget > 0.5) return;
    e.preventDefault();
    wheelDeltaTarget = MathUtils.clamp(wheelDeltaTarget + e.deltaY * 0.00015, -2, 2);
    wheelDirection = e.deltaY > 0 ? 1 : -1;
    lastInteractTime = performance.now();
  }, { passive: false });

  function setDim(v: number): void {
    tween.dimTarget = v;
    if (v) {
      hoveredIndex = -1;
      onHover?.(null, null);
      canvas!.style.cursor = "grab";
    }
  }

  function focus(i: number): void {
    scrollOffset = centerIndex + i;
  }

  // ── change spiral size ───────────────────────────────────────────────────────────────────
  function resize(): void {
    const w = canvas!.clientWidth || window.innerWidth;
    const h = canvas!.clientHeight || window.innerHeight;
    renderer.setSize(w, h, false);
    layout = layoutFor(w, h);
    camera.aspect = w / h;
    camera.fov = layout.fov;
    camera.position.z = layout.camZ;
    camera.updateProjectionMatrix();
    group.position.x = layout.groupX;
    cards.forEach((mesh) => {
      mesh.scale.set(layout.cardW, layout.cardH, 1);
      (mesh.material as ShaderMaterial).uniforms["uBend"].value = layout.bend;
    });
  }
  resize();
  new ResizeObserver(resize).observe(canvas);

  // pause rendering when off-screen cos not everyone has a 5090
  let isVisible = true;
  new IntersectionObserver(([entry]) => { isVisible = entry.isIntersecting; }, { threshold: 0 }).observe(canvas);

  let prevFrameTime = performance.now();

  function frame(now: number): void {
    requestAnimationFrame(frame);
    if (!isVisible || document.hidden) { prevFrameTime = now; return; }

    const dt = Math.min((now - prevFrameTime) / 1000, 0.05);
    prevFrameTime = now;

    // animate dim state
    tween.dim += (tween.dimTarget - tween.dim) * 0.12;
    const hiddenTarget = tween.dimTarget > 0.5 ? 1 : 0;

    // smooth scroll and apply auto-rotate when idle
    wheelDelta += (wheelDeltaTarget - wheelDelta) * 0.1;
    scrollOffset += wheelDelta;
    if (!isPointerDown && now - lastInteractTime > 700 && hiddenTarget < 0.5) {
      if (Math.abs(wheelDeltaTarget) < 0.002) wheelDeltaTarget = wheelDirection * AUTO_ROTATE;
      scrollOffset += AUTO_ROTATE * dt * 60;
    }
    if (!isPointerDown) wheelDeltaTarget *= 0.9;

    // ── per-card update ──────────────────────────────────────────────────────
    const r = layout.radius;

    cards.forEach((mesh) => {
      const d = mesh.userData as CardData;

      d.hidden += (hiddenTarget - d.hidden) * 0.08;

      // compute world position
      let slot = d.slot - scrollOffset;
      slot = ((slot % totalCount) + totalCount) % totalCount;
      const offset = slot - centerIndex;

      const hideShift = d.hidden * (hiddenTarget > 0.5 ? 1.5 : -1.5);
      const y = offset * layout.vertGap + layout.yOffset - hideShift;
      const rad = r * (1 - d.hidden * 0.5);
      const ang = offset * layout.angGap;

      mesh.position.set(Math.cos(ang) * rad, y, Math.sin(ang) * rad);
      mesh.rotation.set(0, -ang + PI / 2, 0);

      // depth in [0, 1]: 0 = back of cylinder, 1 = front-facing
      const depth = MathUtils.clamp((mesh.position.z + r) / (2 * r), 0, 1);

      // animate intro stagger
      const introShow = REDUCED
        ? 1
        : MathUtils.clamp(tween.reveal * (centerIndex + 1) - Math.abs(offset), 0, 1);

      // top fades near the top, bottom fades per layoutFor thresholds
      const topFade = 1 - MathUtils.smoothstep(y, layout.viewHalfH * 0.6, layout.viewHalfH * 0.98);
      const vfade = topFade * MathUtils.smoothstep(y, layout.fadeBot1, layout.fadeBot0);

      // one-way transparency, cover up the grid lines cos it looks like shit otherwise
      const targetAlpha = introShow * vfade * MathUtils.lerp(0.4, 1.0, depth) * (1 - tween.dim * 0.88);
      d.alpha += (targetAlpha - d.alpha) * 0.12;

      const targetHover = (d.i === hoveredIndex && hiddenTarget < 0.5) ? 1 : 0;
      d.hover += (targetHover - d.hover) * 0.16;
      const hoverScale = 1 + d.hover * 0.05;
      mesh.scale.set(layout.cardW * hoverScale, layout.cardH * hoverScale, 1);

      mesh.renderOrder = Math.round(depth * 100);

      const u = (mesh.material as ShaderMaterial).uniforms;
      u["uShow"].value        = d.alpha;
      u["uHover"].value       = d.hover;
      u["uDepth"].value       = depth;
      u["uReveal"].value      = introShow * (1 - d.hidden * 0.85);
      u["uScrollSpeed"].value = wheelDelta;
    });

    // ── detect cover ──────────────────────────────────────────────────────
    if (hiddenTarget < 0.5 && !isPointerDown) {
      const hit = pickCard();
      if (hit !== hoveredIndex) {
        hoveredIndex = hit;
        onHover?.(hit >= 0 ? items[hit] : null, hit >= 0 ? cards[hit] : null);
        canvas!.style.cursor = hit >= 0 ? "pointer" : "grab";
      }
    }

    renderer.render(scene, camera);
  }
  requestAnimationFrame(frame);

  function destroy(): void {
    cards.forEach((m) => (m.material as ShaderMaterial).dispose());
    cardGeo.dispose();
    renderer.dispose();
  }

  const api: SpiralApi = { tween, items, setDim, focus, destroy };
  return api;
}
